import { useCallback, useMemo } from 'react';
import type { FormField } from '@elfsight/widget-form-builder/widget';
import {
  DATA_SERVICE_URL,
  Form as FormContainer
} from '@elfsight/widget-form-builder/widget';
import { getSiteURL } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useEmailSubmitPopup } from '../../../use-email-submit-popup';
import { FormLayout } from './form-layout';

const CLOSE_POPUP_ACTIONS = ['hide', 'redirect'];

const submitMetadata = {
  type: 'calculator',
  webpage: decodeURIComponent(getSiteURL('page')),
  integrations: undefined
};

export type FormProps = {
  fields: FormField[];
};

export function Form({ fields }: FormProps) {
  const { t } = useLingui();

  const {
    widgetToken,
    submitAction,
    submitRedirectURL,
    integrationSettingsID,
    language
  } = useSettings();

  const { closeFormPopup } = useEmailSubmitPopup();

  const onFormSubmit = useCallback(() => {
    if (CLOSE_POPUP_ACTIONS.includes(submitAction)) {
      closeFormPopup();
    }
  }, [closeFormPopup, submitAction]);

  const formSettings = useMemo(
    () => ({
      submitAction,
      submitRedirectURL,
      integrationSettingsID,
      widgetToken,
      fields,
      submitAdditionalData: submitMetadata
    }),
    [
      submitAction,
      submitRedirectURL,
      integrationSettingsID,
      widgetToken,
      fields
    ]
  );

  return (
    <FormContainer
      {...formSettings}
      onSubmitSuccessful={onFormSubmit}
      submitUrl={`${DATA_SERVICE_URL}/public/resources/form-submissions/`}
      submitError={t`An error occurred when submitting the form, please try again later`}
      consentValues={[t`Yes`, t`No`]}
      widgetLanguage={language.language}
    >
      <FormLayout fields={fields} />
    </FormContainer>
  );
}
